import { Version } from '../../../util/Versions';
import questionnaireUtils from './questionnaireUtils';
import surveyUrls from './surveyUrls';
import {
  responseMatchesSurvey,
  SurveyMatchTarget,
} from './getInProgressResponsesForSurvey';

export type ClinicSurveyLaunchKind =
  | 'not-started'
  | 'in-progress'
  | 'completed';

export type ClinicSurveyLaunchStatus = {
  kind: ClinicSurveyLaunchKind;
  lastEditedAt?: string;
  completedAt?: string;
  /**
   * Newest completed response for this survey lineage (matched the same way
   * as the status itself). Present even when an in-progress draft wins the
   * status so callers can offer a read-only view of the last submission.
   */
  latestCompletedResponse?: fhir.QuestionnaireResponse;
  /**
   * Every completed response for this survey lineage, newest first (same
   * matching and sort as `latestCompletedResponse`, which is always
   * `completedResponses[0]`). Renewing members complete these surveys
   * annually, so multiple completions are expected over time.
   */
  completedResponses: fhir.QuestionnaireResponse[];
};

export const hasQuickLaunchExtension = (
  questionnaire?: fhir.Questionnaire | null,
): boolean => {
  if (!questionnaire) {
    return false;
  }
  const extensions = [
    ...(questionnaire.modifierExtension ?? []),
    ...(questionnaire.extension ?? []),
  ];
  return extensions.some(extension => extension.url === surveyUrls.quickLaunch);
};

/**
 * Author-declared display order for the clinic quick-launch table, read from
 * the quick-launch extension's value (valueInteger, or valueDecimal). The
 * convention is ascending weight: lower values render first. A flag-only
 * extension (no value) or a non-finite value returns MAX_SAFE_INTEGER so
 * unweighted surveys sort last, keeping existing environments unchanged
 * until an author opts in.
 */
export const getQuickLaunchOrder = (
  questionnaire?: fhir.Questionnaire | null,
): number => {
  if (!questionnaire) {
    return Number.MAX_SAFE_INTEGER;
  }
  const extensions = [
    ...(questionnaire.modifierExtension ?? []),
    ...(questionnaire.extension ?? []),
  ];
  for (const extension of extensions) {
    if (extension.url !== surveyUrls.quickLaunch) {
      continue;
    }
    const value = extension.valueInteger ?? extension.valueDecimal;
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return Number.MAX_SAFE_INTEGER;
};

/**
 * Author-declared short display label for the clinic quick-launch table, read
 * from the quick-launch-label extension's valueString. Returns undefined when
 * the extension is absent or its value is empty/whitespace, so callers fall
 * back to the survey's real name. Only the quick-launch table uses this —
 * the Surveys tab, builder, and everything else keep the real name.
 */
export const getQuickLaunchLabel = (
  questionnaire?: fhir.Questionnaire | null,
): string | undefined => {
  if (!questionnaire) {
    return undefined;
  }
  const extensions = [
    ...(questionnaire.modifierExtension ?? []),
    ...(questionnaire.extension ?? []),
  ];
  for (const extension of extensions) {
    if (extension.url !== surveyUrls.quickLaunchLabel) {
      continue;
    }
    const label = extension.valueString?.trim();
    if (label) {
      return label;
    }
  }
  return undefined;
};

const timestamp = (iso?: string): number => {
  if (!iso) {
    return 0;
  }
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const responseLastEditedAt = (
  response: fhir.QuestionnaireResponse,
): string | undefined => response.meta?.lastUpdated || response.authored;

export const getClinicSurveyLaunchStatus = (
  survey: SurveyMatchTarget,
  responses: fhir.QuestionnaireResponse[] | undefined,
): ClinicSurveyLaunchStatus => {
  const matched = (responses ?? []).filter(response =>
    responseMatchesSurvey(response, survey),
  );
  const inProgress = matched
    .filter(response => response.status === 'in-progress')
    .sort(
      (left, right) =>
        timestamp(responseLastEditedAt(right)) -
        timestamp(responseLastEditedAt(left)),
    );
  const completed = matched
    .filter(response => response.status === 'completed')
    .sort(
      (left, right) => timestamp(right.authored) - timestamp(left.authored),
    );

  if (inProgress[0]) {
    return {
      kind: 'in-progress',
      lastEditedAt: responseLastEditedAt(inProgress[0]),
      completedAt: completed[0]?.authored,
      latestCompletedResponse: completed[0],
      completedResponses: completed,
    };
  }
  if (completed[0]) {
    return {
      kind: 'completed',
      completedAt: completed[0].authored,
      latestCompletedResponse: completed[0],
      completedResponses: completed,
    };
  }
  return { kind: 'not-started', completedResponses: [] };
};

const normalizeClinicSurveyTitle = (title?: string): string =>
  (title ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

const itemHasCollapsibleSection = (
  item?: fhir.QuestionnaireItem | null,
): boolean => {
  if (!item) {
    return false;
  }
  const extensions = [
    ...(item.extension ?? []),
    ...(item.modifierExtension ?? []),
  ];
  if (
    extensions.some(
      extension => extension.url === surveyUrls.collapsibleSection,
    )
  ) {
    return true;
  }
  return (item.item ?? []).some(itemHasCollapsibleSection);
};

export const questionnaireHasCollapsibleSection = (
  questionnaire?: fhir.Questionnaire | null,
): boolean => (questionnaire?.item ?? []).some(itemHasCollapsibleSection);

const primarySurveyTag = (questionnaire: fhir.Questionnaire): string | null =>
  questionnaireUtils.getPrimarySurveyId(questionnaire);

/**
 * Strongest stable identity for a clinic quick-launch row, matching how the
 * Surveys list groups versions via PrimarySurvey (primary-survey-id tag), then
 * FHIR canonical url/name. Title is intentionally not used here — title twins
 * are collapsed in a second pass so version sets are not mixed with unrelated
 * same-title uploads until after the version pick.
 */
export const getClinicSurveyStrongIdentityKey = (
  questionnaire: fhir.Questionnaire,
  fallbackIndex = 0,
): string => {
  const primarySurveyId = primarySurveyTag(questionnaire);
  if (primarySurveyId) {
    return `primary:${primarySurveyId}`;
  }
  const canonicalUrl = questionnaire.url?.trim();
  if (canonicalUrl) {
    const name = questionnaire.name?.trim();
    return name ? `url:${canonicalUrl}|name:${name}` : `url:${canonicalUrl}`;
  }
  const name = questionnaire.name?.trim();
  if (name) {
    return `name:${name}`;
  }
  return `singleton:${questionnaire.id ?? fallbackIndex}`;
};

const compareQuestionnaireVersions = (
  left: fhir.Questionnaire,
  right: fhir.Questionnaire,
): number => {
  const leftVersion = new Version(left.version ?? '');
  const rightVersion = new Version(right.version ?? '');
  if (leftVersion.isValid && rightVersion.isValid) {
    return leftVersion.sortWith(rightVersion);
  }
  if (leftVersion.isValid !== rightVersion.isValid) {
    return Number(leftVersion.isValid) - Number(rightVersion.isValid);
  }
  return 0;
};

/**
 * Rank by responses that point at this questionnaire id specifically.
 * `getClinicSurveyLaunchStatus` / `responseMatchesSurvey` also match via
 * primarySurveyId, so every active version of a survey looks "completed" once
 * any sibling has a response — that must not decide Launch target.
 */
const directResponseStatusRank = (
  questionnaire: fhir.Questionnaire,
  responses?: fhir.QuestionnaireResponse[],
): number => {
  if (!questionnaire.id) {
    return 0;
  }
  let best = 0;
  for (const response of responses ?? []) {
    if (
      questionnaireUtils.surveyIdFromResponse(response) !== questionnaire.id
    ) {
      continue;
    }
    if (response.status === 'in-progress') {
      return 2;
    }
    if (response.status === 'completed') {
      best = Math.max(best, 1);
    }
  }
  return best;
};

/**
 * Prefer the questionnaire that should back Launch / Completed: a response
 * that references this questionnaire id first (not merely the same
 * primarySurveyId), then highest Questionnaire.version (Surveys-list
 * PrimarySurvey rule), then newest lastUpdated. Collapsible chrome remains a
 * final tie-break for plain vs collapsible uploads.
 */
export const pickPreferredClinicSurvey = (
  candidates: fhir.Questionnaire[],
  responses?: fhir.QuestionnaireResponse[],
): fhir.Questionnaire => {
  if (candidates.length === 1) {
    return candidates[0];
  }
  return [...candidates].sort((left, right) => {
    const byDirectResponse =
      directResponseStatusRank(right, responses) -
      directResponseStatusRank(left, responses);
    if (byDirectResponse !== 0) {
      return byDirectResponse;
    }
    const byVersion = compareQuestionnaireVersions(left, right);
    if (byVersion !== 0) {
      // Ascending comparator: prefer higher version → reverse.
      return -byVersion;
    }
    const byUpdated =
      timestamp(right.meta?.lastUpdated) - timestamp(left.meta?.lastUpdated);
    if (byUpdated !== 0) {
      return byUpdated;
    }
    return (
      Number(questionnaireHasCollapsibleSection(right)) -
      Number(questionnaireHasCollapsibleSection(left))
    );
  })[0];
};

const collapseByKey = (
  surveys: fhir.Questionnaire[],
  keyFor: (survey: fhir.Questionnaire, index: number) => string,
  responses?: fhir.QuestionnaireResponse[],
): fhir.Questionnaire[] => {
  const groups = new Map<string, fhir.Questionnaire[]>();
  const groupOrder: string[] = [];
  surveys.forEach((survey, index) => {
    const key = keyFor(survey, index);
    const existing = groups.get(key);
    if (existing) {
      existing.push(survey);
    } else {
      groups.set(key, [survey]);
      groupOrder.push(key);
    }
  });
  return groupOrder.map(key =>
    pickPreferredClinicSurvey(groups.get(key) ?? [], responses),
  );
};

/**
 * One row per logical clinic survey across paginated scans.
 *
 * 1. Drop pagination id twins.
 * 2. Collapse LifeOmic versions / FHIR canonical identity (primarySurveyId →
 *    url+name → name) — same grouping the Surveys list uses via PrimarySurvey.
 * 3. Collapse remaining same-title twins (plain vs collapsible uploads).
 *
 * Preference inside a group keeps Launch pointed at a response-bearing
 * questionnaire when one exists, else the latest version. Re-running as pages
 * append cannot grow a group back into multiple rows (keys are stable).
 */
export const dedupeClinicQuickLaunchSurveys = (
  surveys: fhir.Questionnaire[],
  responses?: fhir.QuestionnaireResponse[],
): fhir.Questionnaire[] => {
  const seenIds = new Set<string>();
  const uniqueById: fhir.Questionnaire[] = [];
  for (const survey of surveys) {
    if (survey.id) {
      if (seenIds.has(survey.id)) {
        continue;
      }
      seenIds.add(survey.id);
    }
    uniqueById.push(survey);
  }

  const byStrongIdentity = collapseByKey(
    uniqueById,
    (survey, index) => getClinicSurveyStrongIdentityKey(survey, index),
    responses,
  );

  return collapseByKey(
    byStrongIdentity,
    (survey, index) => {
      const titleKey = normalizeClinicSurveyTitle(survey.title);
      return titleKey || `id:${survey.id ?? index}`;
    },
    responses,
  );
};

/**
 * Order quick-launch rows by author-declared weight (ascending), unweighted
 * surveys last. Ties (including all-unweighted environments) break by
 * normalized title so the table stays deterministic regardless of API return
 * order. Returns a new array; input is not mutated.
 */
export const sortClinicQuickLaunchSurveys = (
  surveys: fhir.Questionnaire[],
): fhir.Questionnaire[] =>
  [...surveys].sort((left, right) => {
    const byOrder = getQuickLaunchOrder(left) - getQuickLaunchOrder(right);
    if (byOrder !== 0) {
      return byOrder;
    }
    return normalizeClinicSurveyTitle(left.title).localeCompare(
      normalizeClinicSurveyTitle(right.title),
    );
  });
